/**
 * Environment Data Validator
 *
 * Validates that API response data matches expected environment
 * Prevents cross-environment data leakage
 *
 * Created: October 10, 2025
 * Purpose: Fix recurring cross-environment data leakage issue
 */

export interface EnvironmentDataValidation {
  valid: boolean;
  violations: string[];
  environment: 'development' | 'staging' | 'production';
  violationCount: number;
}

/**
 * Expected storage buckets for each environment
 */
const EXPECTED_BUCKETS = {
  development: 'nna_registry_assets_dev',
  staging: 'nna_registry_assets_stg',
  production: 'nna_registry_assets_prod',
} as const;

/**
 * Validates that API response data matches expected environment
 *
 * @param responseData - The API response data to validate
 * @param expectedEnvironment - The environment the data should be from
 * @returns Validation result with any violations found
 */
export function validateEnvironmentData(
  responseData: any,
  expectedEnvironment: 'development' | 'staging' | 'production'
): EnvironmentDataValidation {
  const violations: string[] = [];
  const expectedBucket = EXPECTED_BUCKETS[expectedEnvironment];

  /**
   * Check a single URL for cross-environment bucket usage
   */
  const checkUrl = (url: string, context: string): void => {
    if (!url || typeof url !== 'string') return;

    // Detect bucket in URL
    const bucketMatch = url.match(/storage\.googleapis\.com\/([^\/]+)\//);
    if (!bucketMatch) return;

    const actualBucket = bucketMatch[1];

    // Check for cross-environment bucket (is it a known bucket but wrong one?)
    const isKnownBucket = Object.values(EXPECTED_BUCKETS).includes(actualBucket as any);
    const isWrongBucket = actualBucket !== expectedBucket;

    if (isKnownBucket && isWrongBucket) {
      violations.push(
        `[${context}] Cross-environment bucket detected: Expected '${expectedBucket}', got '${actualBucket}' in URL: ${url.substring(0, 100)}...`
      );
    }
  };

  /**
   * Recursively check an asset object for storage URLs
   */
  const checkAsset = (asset: any, index?: number): void => {
    if (!asset || typeof asset !== 'object') return;

    const prefix = index !== undefined ? `Asset[${index}]` : 'Asset';

    // Check metadata / primary media URLs (replaces deprecated gcpStorageUrl)
    if (asset.metadata?.videoUrl) {
      checkUrl(asset.metadata.videoUrl, `${prefix}.metadata.videoUrl`);
    }
    if (asset.metadata?.fileUrl) {
      checkUrl(asset.metadata.fileUrl, `${prefix}.metadata.fileUrl`);
    }

    // Check file URLs
    if (asset.files && Array.isArray(asset.files)) {
      asset.files.forEach((file: any, fileIndex: number) => {
        if (file.url) {
          checkUrl(file.url, `${prefix}.files[${fileIndex}].url`);
        }
        if (file.thumbnailUrl) {
          checkUrl(file.thumbnailUrl, `${prefix}.files[${fileIndex}].thumbnailUrl`);
        }
      });
    }

    // Check albumArt URL
    if (asset.albumArt) {
      checkUrl(asset.albumArt, `${prefix}.albumArt`);
    }

    // Check songMetadata.albumArtUrl
    if (asset.songMetadata && typeof asset.songMetadata === 'object') {
      const songMetadata = typeof asset.songMetadata === 'string'
        ? JSON.parse(asset.songMetadata)
        : asset.songMetadata;

      if (songMetadata.albumArtUrl) {
        checkUrl(songMetadata.albumArtUrl, `${prefix}.songMetadata.albumArtUrl`);
      }
    }
  };

  // Check response data structure
  if (responseData && typeof responseData === 'object') {
    // Array of assets
    if (Array.isArray(responseData)) {
      responseData.forEach((asset, index) => checkAsset(asset, index));
    }

    // Paginated response with data array
    else if (responseData.data && Array.isArray(responseData.data)) {
      responseData.data.forEach((asset: any, index: number) => checkAsset(asset, index));
    }

    // Single asset response
    else if (responseData.data && typeof responseData.data === 'object') {
      checkAsset(responseData.data);
    }

    // Single asset object (not wrapped)
    else if (responseData.metadata || responseData.files) {
      checkAsset(responseData);
    }
  }

  return {
    valid: violations.length === 0,
    violations,
    environment: expectedEnvironment,
    violationCount: violations.length,
  };
}

/**
 * Log validation violations with appropriate severity
 */
export function logValidationViolations(
  validation: EnvironmentDataValidation,
  context: string
): void {
  if (!validation.valid) {
    console.error(`🚨 [ENVIRONMENT VIOLATION] Cross-environment data detected in ${context}:`, {
      environment: validation.environment,
      violationCount: validation.violationCount,
      violations: validation.violations,
    });
  }
}

/**
 * Determine action to take based on environment and violations
 *
 * @param validation - The validation result
 * @param currentEnvironment - Current environment
 * @returns Action to take: 'block' | 'normalize' | 'allow'
 */
export function determineValidationAction(
  validation: EnvironmentDataValidation,
  currentEnvironment: 'development' | 'staging' | 'production'
): 'block' | 'normalize' | 'allow' {
  if (validation.valid) {
    return 'allow';
  }

  // Production: Strict mode - block cross-environment data
  if (currentEnvironment === 'production') {
    return 'block';
  }

  // Development/Staging: Permissive mode - normalize and warn
  return 'normalize';
}
